import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const services = [
  {
    icon: 'ri-home-4-line',
    title: 'Luxury Home Painting',
    description: 'Premium interior and exterior painting for Sunningdale estates',
    price: 'From R15,000',
    features: ['Designer finishes', 'Premium materials', 'Expert craftsmanship', '3-year warranty']
  },
  {
    icon: 'ri-paint-brush-line',
    title: 'Decorative Finishes',
    description: 'Specialized decorative painting and custom color matching',
    price: 'From R18,000',
    features: ['Custom colors', 'Textured finishes', 'Feature walls', 'Artistic details']
  },
  {
    icon: 'ri-building-line',
    title: 'Estate Maintenance',
    description: 'Comprehensive painting services for residential estates',
    price: 'From R25,000',
    features: ['Multi-unit coordination', 'Scheduled maintenance', 'Quality assurance', 'Long-term warranty']
  }
];

const paintingProcess = [
  { step: '1', title: 'Consultation', description: 'Detailed assessment and color consultation' },
  { step: '2', title: 'Preparation', description: 'Meticulous surface prep and property protection' },
  { step: '3', title: 'Expert Application', description: 'Premium painting with attention to detail' },
  { step: '4', title: 'Quality Assurance', description: 'Thorough inspection and client walkthrough' }
];

const testimonials = [
  {
    rating: 5,
    text: 'Exceptional service and quality! The team transformed our Sunningdale home with beautiful finishes. Their attention to detail and professionalism was outstanding.',
    name: 'Michael Thompson',
    property: 'Sunningdale Estate',
    image: 'https://public.readdy.ai/ai/img_res/aaaa856893d67fbc5fe34c7c68131ad5.jpg'
  },
  {
    rating: 5,
    text: 'We are thrilled with the results! The painters were skilled, respectful, and delivered premium quality work. Our home looks absolutely stunning.',
    name: 'Catherine Williams',
    property: 'Luxury Homeowner',
    image: 'https://readdy.ai/api/search-image?query=Professional%20South%20African%20woman%20homeowner%20portrait%20smiling%2C%20natural%20lighting%2C%20realistic%20photography&width=100&height=100&seq=testimonial-sunningdale-002&orientation=squarish'
  },
  {
    rating: 5,
    text: 'Outstanding craftsmanship and service. They understood our vision and executed it perfectly. Highly recommend for any luxury painting project.',
    name: 'David Robertson',
    property: 'Sunningdale Resident',
    image: 'https://readdy.ai/api/search-image?query=Professional%20South%20African%20businessman%20portrait%20smiling%2C%20natural%20lighting%2C%20realistic%20photography&width=100&height=100&seq=testimonial-sunningdale-003&orientation=squarish'
  }
];

export default function SunningdalePage() {
  useEffect(() => {
    document.title = 'Sunningdale Painting Services | Professional Painters Cape Town';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert painting services in Sunningdale, Cape Town. Residential, commercial, and estate painting solutions. Professional painters with 15+ years experience. Free quotes available.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Sunningdale painters, painting services Sunningdale, house painters Sunningdale, estate painters Cape Town, residential painting');
    }

    const metaGeoPosition = document.querySelector('meta[name="geo.position"]');
    if (metaGeoPosition) {
      metaGeoPosition.setAttribute('content', '-33.7892;18.6234');
    }

    const metaGeoPlacename = document.querySelector('meta[name="geo.placename"]');
    if (metaGeoPlacename) {
      metaGeoPlacename.setAttribute('content', 'Sunningdale, Cape Town, Western Cape, South Africa');
    }

    const metaGeoRegion = document.querySelector('meta[name="geo.region"]');
    if (metaGeoRegion) {
      metaGeoRegion.setAttribute('content', 'ZA-WC');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20modern%20home%20in%20Sunningdale%20Cape%20Town%2C%20skilled%20painters%20with%20equipment%20applying%20paint%20to%20house%20exterior%2C%20beautiful%20residential%20property%2C%20sunny%20day%2C%20professional%20service%2C%20high%20quality%20workmanship%2C%20realistic%20photography&width=1920&height=1080&seq=sunningdale-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Sunningdale
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert painting services for Sunningdale's residential and commercial properties. Quality craftsmanship and reliable service for your home or business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </Link>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Area Description */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Painting Experts Serving Sunningdale</h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Sunningdale is one of Cape Town's most sought-after residential areas, featuring luxury estates, golf course properties, and upscale homes with stunning mountain and ocean views. Our professional painting services are tailored to meet the high standards expected in this prestigious Sunningdale community.
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                With over 15 years of experience providing Sunningdale painting services, we specialize in premium finishes for luxury homes, estate properties, and high-end residential complexes. Our team understands the importance of quality craftsmanship and attention to detail that Sunningdale properties demand.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                From elegant interior painting to weather-resistant exterior coatings, we use only the finest materials and advanced techniques to protect and enhance your Sunningdale property. Our services include specialized finishes, decorative painting, and comprehensive maintenance solutions for discerning homeowners.
              </p>
            </div>
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20team%20working%20on%20luxury%20home%20exterior%20in%20Sunningdale%20estate%2C%20showing%20skilled%20craftsmen%20applying%20premium%20paint%20with%20precision%2C%20elegant%20house%20facade%2C%20high-end%20painting%20equipment%2C%20professional%20service%20photography%20with%20natural%20lighting&width=600&height=500&seq=sunningdale-team-002&orientation=portrait"
                alt="Professional Painters in Sunningdale"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Offered */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Sunningdale Painting Services</h2>
            <p className="text-xl text-gray-600">Premium painting solutions for luxury properties</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-black font-semibold text-lg mb-4">{service.price}</div>
                <ul className="text-sm text-gray-600 space-y-1">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <i className="ri-check-line mr-2" style={{color: '#ffff00'}}></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Sunningdale Residents Choose Us</h2>
            <p className="text-xl text-gray-600">Premium painting services for discerning homeowners</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-award-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">15+ Years Experience</h3>
              <p className="text-gray-600">Extensive experience with luxury Sunningdale properties</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-vip-crown-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Premium Materials</h3>
              <p className="text-gray-600">Only the finest paints and coatings for lasting beauty</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-team-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Expert Craftsmen</h3>
              <p className="text-gray-600">Highly skilled painters with attention to detail</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-shield-check-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Quality Guarantee</h3>
              <p className="text-gray-600">Comprehensive warranty on all workmanship</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Painting Process</h2>
            <p className="text-xl text-gray-600">Professional service from consultation to completion</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold" style={{backgroundColor: '#ffff00'}}>
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Recent Projects in Sunningdale</h2>
            <p className="text-xl text-gray-600">Excellence in every project we undertake</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Luxury%20modern%20home%20exterior%20with%20premium%20white%20and%20beige%20paint%20finish%20in%20Sunningdale%20estate%2C%20showing%20elegant%20architecture%2C%20manicured%20landscaping%2C%20mountain%20backdrop%2C%20professional%20high-end%20painting%20work%2C%20golden%20hour%20lighting&width=400&height=300&seq=sunningdale-project-001&orientation=landscape"
                  alt="Luxury Home Painting"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Estate Home Exterior</h3>
              <p className="text-gray-600">Premium exterior painting with designer color palette</p>
            </div>
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20interior%20living%20space%20with%20freshly%20painted%20walls%20in%20sophisticated%20neutral%20tones%2C%20showing%20luxury%20furniture%2C%20high%20ceilings%2C%20large%20windows%2C%20professional%20interior%20painting%20in%20Sunningdale%20luxury%20home&width=400&height=300&seq=sunningdale-project-002&orientation=landscape"
                  alt="Interior Painting Project"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Designer Interior</h3>
              <p className="text-gray-600">Sophisticated interior finishes with custom color matching</p>
            </div>
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Residential%20estate%20complex%20in%20Sunningdale%20with%20freshly%20painted%20buildings%2C%20showing%20multiple%20luxury%20units%2C%20coordinated%20color%20scheme%2C%20pristine%20condition%2C%20professional%20estate%20painting%20work%2C%20clear%20blue%20sky&width=400&height=300&seq=sunningdale-project-003&orientation=landscape"
                  alt="Estate Complex Painting"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Estate Complex</h3>
              <p className="text-gray-600">Complete estate painting with coordinated finishes</p>
            </div>
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Luxury%20home%20roof%20with%20premium%20waterproof%20coating%20in%20Sunningdale%2C%20showing%20tiled%20roof%20with%20protective%20finish%2C%20mountain%20views%2C%20quality%20roofing%20work%2C%20residential%20property%20maintenance%2C%20natural%20lighting&width=400&height=300&seq=sunningdale-project-004&orientation=landscape"
                  alt="Roof Coating Project"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Roof Protection</h3>
              <p className="text-gray-600">Premium waterproof coating with thermal protection</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Sunningdale Clients Say</h2>
            <p className="text-xl text-gray-600">Trusted by Sunningdale's discerning homeowners</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-lg" style={{color: '#ffff00'}}></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{backgroundColor: '#ffff00'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Transform Your Sunningdale Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Sunningdale's trusted painting professionals. Get a free quote for your residential or commercial property.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-black text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            Get Free Quote
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}